import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  GitBranch,
  Search,
  Database,
  ChevronRight,
  X,
  Wand2
} from "lucide-react";
import { planWorkflow, compileWorkflow } from "../../services/aiWorkflowService";
import { createWorkflow } from "../../services/workflowService";
import { useEndpoints } from "../../hooks/useEndpoints";
import GraphView from "./GraphView";

const SUGGESTED_PROMPTS = [
  "When I enter a vehicle's VIN, tell me whether it's a heavy truck and pull the right specs for it.",
  "For a property address, check whether it's in a flood zone and whether there's earthquake risk nearby.",
  "Look up a commercial vehicle from its VIN and give me its specs and current market value."
];

const PLAN_ERROR =
  "I couldn't put together a plan for that just now. Try describing it a little differently, or break it into simpler steps.";
const CREATE_ERROR =
  "Something went wrong while creating this workflow. Try refining it in the chat, then create again.";

/** snake_case / kebab identifiers → human Title Case; leave already-spaced names alone. */
function prettify(s) {
  if (!s) return "";
  if (/\s/.test(s)) return s;
  return s
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** A friendly, human title for a step — the real endpoint name from the catalog
 * when we have it, otherwise a prettified version of the internal step name. */
function stepTitle(step, metaById) {
  if (step.type === "endpoint_call") {
    const m = metaById[step.endpoint_id];
    return (m && (m.endpoint_name || m.api_name)) || prettify(step.name);
  }
  return prettify(step.name);
}

/** Shape a (not-yet-compiled) plan into the definition GraphView renders, so the
 * builder shows the same flow canvas as the Studio/detail pages. Condition text
 * is plain English, so the branch edge labels read naturally. */
function planToDefinition(plan) {
  const steps = {};
  for (const s of plan.steps || []) {
    if (s.type === "endpoint_call") {
      steps[s.name] = {
        type: "endpoint_call",
        endpoint_id: s.endpoint_id,
        // next_steps is a list — two+ entries fan out and run in parallel.
        // Tolerate the legacy single next_step.
        next:
          s.next_steps && s.next_steps.length
            ? s.next_steps
            : s.next_step
            ? [s.next_step]
            : []
      };
    } else {
      steps[s.name] = {
        type: "branch",
        branches: (s.branches || []).map((b) => ({
          when: b.condition_text || "condition",
          next: b.next_step
        })),
        default_next: s.default_next || null
      };
    }
  }
  return { version: 1, start_step: plan.start_step, steps, inputs: {}, outputs: {} };
}

/**
 * AIBuilderDialog — "Build a workflow with AI", written for non-technical users.
 *
 * Left: a chat panel where the user describes (and refines) what they want, in
 * plain words. Right: the AI's proposed plan, rendered as plain English — no
 * identifiers, templates, or API jargon (those hide behind "technical details").
 * On create we persist the workflow and open its summary page so they can run it.
 */
export default function AIBuilderDialog({ open, onClose }) {
  const navigate = useNavigate();
  const { endpoints } = useEndpoints();
  const metaById = useMemo(
    () => Object.fromEntries((endpoints || []).map((e) => [e.endpoint_id, e])),
    [endpoints]
  );

  const [prompt, setPrompt] = useState("");
  const [history, setHistory] = useState([]);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const chatEndRef = useRef(null);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, planning]);

  useEffect(() => {
    if (open) {
      setPrompt("");
      setHistory([]);
      setPlan(null);
      setPlanning(false);
      setPlanError(null);
      setCreating(false);
      setCreateError(null);
    }
  }, [open]);

  if (!open) return null;

  const sendPrompt = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || planning) return;

    const priorHistory = history;
    setHistory([...priorHistory, { role: "user", content: trimmed }]);
    setPrompt("");
    setPlanning(true);
    setPlanError(null);

    const res = await planWorkflow({ prompt: trimmed, history: priorHistory });
    setPlanning(false);

    if (res.success && res.data) {
      setPlan(res.data.plan);
      setHistory((h) => [
        ...h,
        { role: "assistant", content: res.data.assistant_message }
      ]);
    } else {
      // Keep the real (often technical) error out of the user's face.
      console.error("AI plan failed:", res.message);
      setPlanError(PLAN_ERROR);
    }
  };

  const createFromPlan = async () => {
    if (!plan || creating) return;
    setCreating(true);
    setCreateError(null);

    const compiled = await compileWorkflow(plan);
    if (!compiled.success || !compiled.data?.definition) {
      console.error("AI compile failed:", compiled.message);
      setCreating(false);
      setCreateError(CREATE_ERROR);
      return;
    }

    const created = await createWorkflow({
      name: prettify(plan.name),
      description: plan.description,
      visibility: "org",
      definition: compiled.data.definition
    });
    setCreating(false);

    if (created.success && created.data?.id) {
      onClose();
      // Land on the friendly summary + Run page, not the node/edge editor.
      navigate(`/workflows/${created.data.id}`);
    } else {
      console.error("Create workflow failed:", created.message);
      setCreateError(CREATE_ERROR);
    }
  };

  const discard = () => {
    setPlan(null);
    setHistory([]);
    setPlanError(null);
    setCreateError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900">Build a workflow with AI</h2>
            <p className="text-xs text-gray-500">
              Describe what you want in plain words — you'll get a plan to review before anything is created.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[420px_1fr]">
          {/* Chat panel */}
          <div className="flex flex-col min-h-0 border-r border-gray-200 bg-gray-50">
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
              {history.length === 0 && !planning && (
                <ChatEmptyState onPick={sendPrompt} />
              )}
              {history.map((turn, idx) => (
                <ChatBubble key={idx} role={turn.role} content={turn.content} />
              ))}
              {planning && <ThinkingBubble />}
              {planError && <ChatBubble role="assistant" content={planError} error />}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-200 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  className="flex-1 min-h-[44px] max-h-32 rounded-lg border border-gray-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                  placeholder={
                    history.length === 0
                      ? "Describe what you want…"
                      : 'Refine it… (e.g. "also check the driving record first")'
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendPrompt(prompt);
                    }
                  }}
                  disabled={planning}
                />
                <button
                  onClick={() => sendPrompt(prompt)}
                  disabled={!prompt.trim() || planning}
                  className="flex items-center justify-center h-11 w-11 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                Enter to send · Shift+Enter for new line
              </div>
            </div>
          </div>

          {/* Plan preview */}
          <div className="flex flex-col min-h-0 overflow-hidden bg-white">
            {!plan && !planning && (
              <div className="flex-1 flex items-center justify-center p-8 text-center">
                <div className="max-w-sm">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mb-3">
                    <Wand2 className="w-7 h-7" />
                  </div>
                  <div className="text-sm font-medium text-gray-900 mb-1">No plan yet</div>
                  <div className="text-xs text-gray-500">
                    Tell the AI what you want in the chat. It'll lay out a step-by-step plan
                    here, in plain English, for you to review.
                  </div>
                </div>
              </div>
            )}
            {planning && !plan && (
              <div className="flex-1 flex items-center justify-center p-8">
                <PlanLoadingState />
              </div>
            )}
            {plan && (
              <PlanPreview
                plan={plan}
                metaById={metaById}
                endpoints={endpoints}
                isRefining={planning}
                createError={createError}
                isCreating={creating}
                onCreate={createFromPlan}
                onDiscard={discard}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Chat panel pieces
// ============================================================

function ChatEmptyState({ onPick }) {
  return (
    <div className="text-center py-9 px-2">
      <div className="w-11 h-11 mx-auto rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white mb-4">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="text-[15px] font-medium text-gray-900 mb-1.5">What would you like to set up?</div>
      <div className="text-[13px] text-gray-500 leading-relaxed mb-7 max-w-sm mx-auto">
        Describe what you want to find out or automate, in plain words — and what you'll type
        in each time you run it. The AI turns it into a workflow you can review and run.
      </div>
      <div className="space-y-2.5 text-left">
        <div className="text-[11px] text-gray-400 mb-1">Try one of these</div>
        {SUGGESTED_PROMPTS.map((p) => (
          <button
            key={p}
            onClick={() => onPick(p)}
            className="w-full text-left text-[13px] text-gray-700 leading-relaxed bg-white border border-gray-100 rounded-lg px-3.5 py-2.5 hover:bg-gray-50 hover:border-gray-200 transition-colors"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatBubble({ role, content, error }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-xs ${
          isUser
            ? "bg-gray-900 text-white"
            : error
            ? "bg-red-100 text-red-700"
            : "bg-gradient-to-br from-violet-500 to-blue-500 text-white"
        }`}
      >
        {isUser ? "U" : <Sparkles className="w-3 h-3" />}
      </div>
      <div
        className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-[14px] leading-relaxed ${
          isUser
            ? "bg-gray-900 text-white"
            : error
            ? "bg-red-50 text-red-900 border border-red-200"
            : "bg-white text-gray-800 border border-gray-100"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex gap-2.5">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 shrink-0 flex items-center justify-center text-white">
        <Sparkles className="w-3 h-3" />
      </div>
      <div className="bg-white border border-gray-100 rounded-xl px-3.5 py-2.5 text-[14px] text-gray-500 flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking through the plan…
      </div>
    </div>
  );
}

function PlanLoadingState() {
  const messages = [
    "Looking at the data you can pull…",
    "Choosing the right steps…",
    "Wiring it together…",
    "Double-checking the details…"
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % messages.length), 2000);
    return () => clearInterval(t);
  }, [messages.length]);
  return (
    <div className="text-center">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
      <div className="text-sm font-medium text-gray-900">Building your plan</div>
      <div className="text-xs text-gray-500 mt-1">{messages[idx]}</div>
    </div>
  );
}

// ============================================================
// Plan preview (right panel)
// ============================================================

function PlanPreview({ plan, metaById, endpoints, isRefining, createError, isCreating, onCreate, onDiscard }) {
  const [showTech, setShowTech] = useState(false);
  const [viewMode, setViewMode] = useState("steps"); // "steps" | "flow"
  const [showNotes, setShowNotes] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  // Friendly title for each step, so a branch can name its destinations in words.
  const titleByName = useMemo(() => {
    const m = {};
    (plan.steps || []).forEach((s) => {
      m[s.name] = stepTitle(s, metaById);
    });
    return m;
  }, [plan, metaById]);

  const planDef = useMemo(() => planToDefinition(plan), [plan]);
  const noteCount = plan.warnings?.length || 0;

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-7">
        {/* Hero */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-gray-400 mb-1.5">Proposed workflow</div>
            <h3 className="text-xl font-medium text-gray-900">{prettify(plan.name)}</h3>
            {plan.description && (
              <p className="text-[15px] text-gray-600 mt-1.5 leading-relaxed">{plan.description}</p>
            )}
          </div>
          {isRefining && (
            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700">
              <Loader2 className="w-2.5 h-2.5 animate-spin" /> Updating
            </span>
          )}
        </div>

        {/* Inputs as a single chip line */}
        {plan.inputs?.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] text-gray-400">You'll enter</span>
            {plan.inputs.map((inp) => (
              <span
                key={inp.name}
                title={inp.description || ""}
                className="inline-flex items-center gap-1.5 text-[13px] px-2.5 py-1 rounded-full bg-gray-100 text-gray-800"
              >
                {prettify(inp.name)}
                {inp.required && <span className="w-1 h-1 rounded-full bg-red-400" title="required" />}
              </span>
            ))}
          </div>
        )}

        {/* Steps / Flow */}
        <div>
          <div className="flex items-center justify-between mb-4 gap-2">
            <div className="text-[13px] font-medium text-gray-700">What it does</div>
            <div className="flex items-center gap-3">
              <div className="flex rounded-md border border-gray-200 overflow-hidden text-[11px]">
                <button
                  onClick={() => setViewMode("steps")}
                  className={`px-2.5 py-1 ${
                    viewMode === "steps" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Steps
                </button>
                <button
                  onClick={() => setViewMode("flow")}
                  className={`px-2.5 py-1 ${
                    viewMode === "flow" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  Flow
                </button>
              </div>
              {viewMode === "steps" && (
                <button
                  onClick={() => setShowTech((v) => !v)}
                  className="text-[11px] text-gray-400 hover:text-gray-700"
                >
                  {showTech ? "Hide" : "Show"} details
                </button>
              )}
            </div>
          </div>
          {viewMode === "flow" ? (
            <div
              className="rounded-lg border border-gray-200 overflow-hidden bg-[#FCFCFC]"
              style={{ height: 360, position: "relative" }}
            >
              <GraphView
                definition={planDef}
                endpoints={endpoints}
                interactive={false}
                workflowId="ai-plan-preview"
              />
            </div>
          ) : (
            <div className="space-y-5">
              {(plan.steps || []).map((s) => (
                <PlanStepCard
                  key={s.name}
                  step={s}
                  isStart={s.name === plan.start_step}
                  metaById={metaById}
                  titleByName={titleByName}
                  showTech={showTech}
                />
              ))}
            </div>
          )}
        </div>

        {/* Quiet, collapsed extras — confidence first, caveats on demand */}
        {(noteCount > 0 || plan.rationale) && (
          <div className="border-t border-gray-100 pt-2">
            {noteCount > 0 && (
              <div>
                <button
                  onClick={() => setShowNotes((v) => !v)}
                  className="w-full flex items-center gap-2 py-2 text-[13px] text-gray-500 hover:text-gray-800"
                >
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showNotes ? "rotate-90" : ""}`} />
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                  {noteCount} thing{noteCount === 1 ? "" : "s"} worth checking
                </button>
                {showNotes && (
                  <ul className="ml-6 mb-1 space-y-1.5 text-[13px] text-gray-600 leading-relaxed list-disc list-outside pr-2">
                    {plan.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {plan.rationale && (
              <div>
                <button
                  onClick={() => setShowWhy((v) => !v)}
                  className="w-full flex items-center gap-2 py-2 text-[13px] text-gray-500 hover:text-gray-800"
                >
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showWhy ? "rotate-90" : ""}`} />
                  Why it's set up this way
                </button>
                {showWhy && (
                  <p className="ml-6 mb-1 text-[13px] text-gray-600 leading-relaxed pr-2">{plan.rationale}</p>
                )}
              </div>
            )}
          </div>
        )}

        {createError && (
          <div className="rounded-lg p-3 bg-red-50 border border-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-red-700 shrink-0 mt-0.5" />
              <div className="text-xs text-red-800">{createError}</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-3.5 border-t border-gray-200 flex items-center justify-between gap-2">
        <button
          onClick={onDiscard}
          className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          Start over
        </button>
        <button
          onClick={onCreate}
          disabled={isCreating}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {isCreating ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" /> Create workflow
            </>
          )}
        </button>
      </div>
    </>
  );
}

function PlanStepCard({ step, isStart, metaById, titleByName, showTech }) {
  const isBranch = step.type === "branch";
  const meta = !isBranch ? metaById[step.endpoint_id] : null;
  const source = meta?.provider_name;

  return (
    <div className="flex gap-3.5">
      <div
        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isBranch ? "bg-amber-50 text-amber-700" : "bg-teal-50 text-teal-700"
        }`}
      >
        {isBranch ? <GitBranch className="w-4 h-4" /> : <Search className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-medium text-gray-900">
            {isBranch ? "Decision" : stepTitle(step, metaById)}
          </span>
          {isStart && (
            <span className="text-[10px] font-medium text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
              Starts here
            </span>
          )}
        </div>
        {step.purpose && (
          <div className="text-[13px] text-gray-500 leading-relaxed mt-0.5 mb-2">{step.purpose}</div>
        )}

        {!isBranch && source && (
          <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-blue-50 text-blue-800">
            <Database className="w-3 h-3" /> {source}
          </span>
        )}

        {isBranch && step.branches?.length > 0 && (
          <div className="space-y-2">
            {step.branches.map((b, i) => (
              <div key={i} className="flex items-center gap-2.5 text-[13px] text-gray-800 flex-wrap">
                <span className="font-medium text-amber-700">If {b.condition_text}</span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>{titleByName[b.next_step] || prettify(b.next_step)}</span>
              </div>
            ))}
            {step.default_next && (
              <div className="flex items-center gap-2.5 text-[13px] text-gray-800 flex-wrap">
                <span className="font-medium text-gray-400">Otherwise</span>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>{titleByName[step.default_next] || prettify(step.default_next)}</span>
              </div>
            )}
          </div>
        )}

        {showTech && (
          <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2 space-y-1 font-mono text-[10px] text-gray-500">
            <div>name: {step.name}</div>
            {!isBranch && step.endpoint_id && <div>endpoint_id: {step.endpoint_id}</div>}
            {!isBranch &&
              step.input_mapping &&
              Object.entries(step.input_mapping).map(([k, v]) => (
                <div key={k}>
                  {k} → {v || "—"}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
